import React from 'react';
import { GoogleMapsApiWrapper, GoogleMapApiLoadingStatus } from './GoogleMapsApiWrapper';
import { Loader } from '@googlemaps/js-api-loader';
import { BusinessLocation } from 'model/location';
import { darkMapStyle } from './GoogleMap.utils';
import { useIsDarkTheme } from 'hooks';
const ZOOM_FOCUS = 17;
// TODO: create error and loading components
const render = (status: GoogleMapApiLoadingStatus): React.ReactElement | null => {
  if (status === GoogleMapApiLoadingStatus.LOADING) return <h3>{status} ..</h3>;
  if (status === GoogleMapApiLoadingStatus.FAILURE) return <h3>{status} ...</h3>;
  return null;
};

const Map = ({
  className,
  locations,
  onMarkerClick,
  location
}: {
  className?: string;
  locations?: Array<Partial<BusinessLocation>>;
  location?: google.maps.LatLng;
  onMarkerClick?: (
    loc: Partial<BusinessLocation>,
    e: google.maps.MapsEventListener,
    map: google.maps.Map
  ) => void;
}) => {
  const ref = React.useRef<React.RefObject<HTMLDivElement>>();
  const isDarkMode = useIsDarkTheme();

  React.useEffect(() => {
    // init map
    const map = new window.google.maps.Map(ref.current as any, {
      fullscreenControl: false,
      zoomControl: true,
      streetViewControl: false,
      mapTypeControl: false,
      rotateControl: false,
      ...(isDarkMode ? darkMapStyle : [])
    });

    // fit all locations on the map
    if (map && locations?.length && locations.length > 1) {
      const bounds = locations.reduce((b, l) => {
        if (l.latitude && l.longitude) {
          b.extend({ lat: l.latitude, lng: l.longitude });
        }
        return b;
      }, new google.maps.LatLngBounds());
      map.fitBounds(bounds);
    } else if (locations?.length === 1 && locations[0].latitude && locations[0].longitude) {
      map.setZoom(ZOOM_FOCUS);
      map.setCenter({ lat: locations[0].latitude, lng: locations[0].longitude });
    } else if (map && location) {
      map.setZoom(ZOOM_FOCUS);
      map.setCenter(location);
    }

    if (location) {
      new google.maps.Marker({
        position: location,
        map
      });
    }

    // add markers to the map and handle marker click
    locations?.map(loc => {
      if (loc.latitude && loc.longitude) {
        const marker = new google.maps.Marker({
          position: { lat: loc.latitude, lng: loc.longitude },
          map
        });

        if (onMarkerClick) {
          marker.addListener('click', e => {
            // focus clicked marker on the map and zoom in
            const currLat = e.latLng.lat();
            const currLng = e.latLng.lng();
            map.panTo({ lat: currLat, lng: currLng });
            const currentZoom = map.getZoom();
            if ((currentZoom || 0) < ZOOM_FOCUS) {
              map.setZoom(ZOOM_FOCUS);
            }

            onMarkerClick(loc, e, map);
          });
        }
      }
    });
  }, [locations, onMarkerClick, isDarkMode, location]);

  return <div ref={ref as any} id="map" className={className} />;
};

interface Props {
  className?: string;
  callback?: (status: GoogleMapApiLoadingStatus, loader: Loader) => void;
  onMarkerClick?: (
    location: Partial<BusinessLocation>,
    e: google.maps.MapsEventListener,
    map: google.maps.Map
  ) => void;
  locations?: Array<Partial<BusinessLocation>>;
  location?: google.maps.LatLng;
}

const GoogleMap = ({ location, className, callback, locations, onMarkerClick }: Props) => {
  return (
    <GoogleMapsApiWrapper render={render as any} callback={callback}>
      <Map
        location={location}
        onMarkerClick={onMarkerClick}
        locations={locations}
        className={className}
      />
    </GoogleMapsApiWrapper>
  );
};

export default GoogleMap;
